/**
 * Keeps a stateless popstate from firing `turbo:before-cache`.
 *
 * A fragment navigation (`<a href="#">` without `preventDefault()`, `location.hash = …`) is not a Turbo visit,
 * yet Turbo 8.0.x answers its popstate in `Session#historyPoppedWithEmptyState` by caching a snapshot, which
 * fires `turbo:before-cache` with no `turbo:load` afterwards. The Intercom widget shuts down on
 * `turbo:before-cache` and reboots only on `turbo:load`, so it stays gone and its orphaned listeners later throw
 * `Cannot read properties of null (reading 'document')`. Refs #694109.
 *
 * The override is Turbo's hook minus `view.cacheSnapshot()`. Trade-off: Back to a page left only through a
 * fragment navigation finds no snapshot and requests the page (Turbo's normal uncached restoration).
 * Coupled to Turbo 8.0.x internals; `turboFragmentNavigation.integration.spec.js` is the canary.
 */

export const HOOK_MISSING_WARNING =
  "[turboFragmentNavigation] Turbo session hook missing, fragment-navigation patch inactive";

export interface TurboSessionLike {
  history: { replace(location: URL): void };
  view: { lastRenderedLocation: URL };
  historyPoppedWithEmptyState?: (location: URL) => void;
}

/** Overrides `session.historyPoppedWithEmptyState`; warns and skips when the hook is missing. */
export const patchTurboFragmentNavigation = (session: TurboSessionLike | undefined): void => {
  if (!session || typeof session.historyPoppedWithEmptyState !== "function") {
    console.warn(HOOK_MISSING_WARNING);
    return;
  }

  session.historyPoppedWithEmptyState = location => {
    session.history.replace(location);
    session.view.lastRenderedLocation = location;
  };
};
